"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { FaBars, FaTimes, FaChevronDown, FaPhone } from "react-icons/fa";
import { useIsClient } from "../hooks/useIsClient";
import HydrationSafe from './HydrationSafe';
import { checkStrapiConnection } from "@/lib/strapi-simple";

// Fallback estático para cuando no hay conexión con Strapi
const BRANDS_DATA_FALLBACK = [
  { name: "Farizon", color: "#E67817", logo: "/logos/farizon.png" },
  { name: "Ford", color: "#0B2D50", logo: "/logos/ford.png" },
  { name: "Forland", color: "#00529B", logo: "/logos/forland.png" },
  { name: "Geely", color: "#1E48AB", logo: "/logos/geely.png" },
  { name: "JAC Motors", color: "#CC092F", logo: "/logos/jac.png" },
  { name: "Kia", color: "#BB162B", logo: "/logos/kia.png" },
  { name: "Lincoln", color: "#000000", logo: "/logos/lincoln.png" },
  { name: "Mazda", color: "#101010", logo: "/logos/mazda.png" },
  { name: "Shineray", color: "#D31600", logo: "/logos/shineray.png" },
  { name: "SWM", color: "#1A5336", logo: "/logos/swm.png" }
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isVehiclesOpen, setIsVehiclesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [marcasData, setMarcasData] = useState([]);
  const [isLoadingMarcas, setIsLoadingMarcas] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !isMounted) return;
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 0);
      setShowBackToTop(scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMounted]);

  // Cargar marcas locales y vehículos desde Strapi
  useEffect(() => {
    const loadMarcasData = async () => {
      setIsLoadingMarcas(true);
      try {

        
        // Crear marcas locales fijas
        const marcasLocales = BRANDS_DATA_FALLBACK.map(brand => ({
          nombre: brand.name,
          slug: brand.name.toLowerCase().replace(/\s+/g, '-'),
          logo: brand.logo,
          color: brand.color,
          vehiculos: []
        }));
        
        // Verificar conexión con Strapi para vehículos
        const isConnected = await checkStrapiConnection();
        if (!isConnected) {
          setMarcasData(marcasLocales);
          setSelectedBrand('Mazda');
          setIsLoadingMarcas(false);
          return;
        }
        
        // Obtener vehículos de Strapi
        const { getVehiculos } = await import('@/lib/strapi-simple');
        const vehiculosStrapi = await getVehiculos();
        
        // Agrupar vehículos por marca local
        marcasLocales.forEach(marca => {
          marca.vehiculos = vehiculosStrapi.filter(vehiculo => 
            vehiculo.marca === marca.nombre
          );
        });
        
        setMarcasData(marcasLocales);
        
        // Seleccionar Mazda por defecto
        setSelectedBrand('Mazda');
      } catch (error) {
        // Usar solo marcas locales
        const marcasLocales = BRANDS_DATA_FALLBACK.map(brand => ({
          nombre: brand.name,
          slug: brand.name.toLowerCase().replace(/\s+/g, '-'),
          logo: brand.logo,
          color: brand.color,
          vehiculos: []
        }));
        setMarcasData(marcasLocales);
        setSelectedBrand('Mazda');
      } finally {
        setIsLoadingMarcas(false);
      }
    };

    loadMarcasData();
  }, []);



  const scrollToTop = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Función para verificar si un enlace está activo - optimizada con useCallback
  const isLinkActive = useCallback((href) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  }, [pathname]);

  // Obtener vehículos de la marca seleccionada desde Strapi - TODOS los modelos
  const selectedBrandVehicles = useMemo(() => {
    if (!selectedBrand || marcasData.length === 0) return [];
    
    const marca = marcasData.find(m => m.nombre === selectedBrand);
    if (!marca || !marca.vehiculos) return [];
    
    return marca.vehiculos.map(vehiculo => ({
      id: vehiculo.id,
      name: `${vehiculo.marca} ${vehiculo.modelo}`,
      modelo: vehiculo.modelo,
      price: `$${vehiculo.precio?.toLocaleString() || '0'}`,
      image: vehiculo.imagen_principal || vehiculo.imagen,
      description: vehiculo.descripcion,
      categoria: vehiculo.categoria,
      slug: vehiculo.slug,
      brandSlug: vehiculo.marcaSlug,
      link: `/vehiculos/${vehiculo.marcaSlug}/${vehiculo.slug}`,
    }));
  }, [selectedBrand, marcasData]);

  // Obtener información de la marca seleccionada
  const selectedBrandInfo = useMemo(() => {
    if (!selectedBrand || marcasData.length === 0) return null;
    return marcasData.find(m => m.nombre === selectedBrand);
  }, [selectedBrand, marcasData]);



  // Manejar hover del menú de vehículos
  const handleVehiclesMenuEnter = useCallback(() => {
    setIsVehiclesOpen(true);
  }, []);

  const handleVehiclesMenuLeave = useCallback(() => {
    setIsVehiclesOpen(false);
  }, []);

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          "bg-white shadow-md py-4" 
        }`}
      >
        <div className="container mx-auto px-[5%]">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <img
                src="/logo.png"
                alt="NDC Logo"
                className="h-22 w-auto transition-transform hover:scale-105"
              />
            </Link>

            {/* Menú Principal - Centrado */}
            <div className="hidden flex-1 justify-center lg:flex">
              <ul className="flex items-center space-x-8">
                <li>
                  <Link 
                    href="/" 
                    prefetch={true}
                    className={`text-gray-700 transition-all hover:text-black relative group py-2 ${
                      isLinkActive('/') ? 'text-black font-semibold' : ''
                    }`}
                  >
                    Inicio
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-all duration-300 ${
                        isMounted && isLinkActive('/') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      suppressHydrationWarning={true}
                    ></span>
                  </Link>
                </li>
                <li className="relative group">
                  <button 
                    className={`text-gray-700 transition-all hover:text-black py-2 flex items-center gap-1 ${
                      isLinkActive('/vehiculos') ? 'text-black font-semibold' : ''
                    }`}
                    onMouseEnter={handleVehiclesMenuEnter}
                    onMouseLeave={handleVehiclesMenuLeave}
                  >
                    Vehículos
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor" 
                      className={`w-4 h-4 transition-transform duration-300 ${isMounted && isVehiclesOpen ? 'rotate-180' : ''}`}
                      suppressHydrationWarning={true}
                    >
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-all duration-300 ${
                        isMounted && isLinkActive('/vehiculos') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      suppressHydrationWarning={true}
                    ></span>
                  </button>
                  {/* Menú desplegable enriquecido */}
                  {isMounted && (
                    <div 
                      className={`absolute top-full left-1/2 -translate-x-1/2 w-screen bg-white shadow-lg transition-all duration-300 ${
                        isVehiclesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                      onMouseEnter={handleVehiclesMenuEnter}
                      onMouseLeave={handleVehiclesMenuLeave}
                    >
                    <div className="container mx-auto px-[5%] py-12">
                      <div className="max-w-6xl mx-auto">
                        {/* Todas las marcas */}
                        <div className="mb-10">
                          <h3 className="font-semibold mb-6 text-xl text-center text-gray-800">Nuestras Marcas</h3>
                          {isLoadingMarcas ? (
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
                              {/* 10 marcas fijas siempre visibles */}
                              {BRANDS_DATA_FALLBACK.map((brand) => (
                                <button 
                                  key={brand.name}
                                  onClick={() => setSelectedBrand(brand.name)}
                                  className={`px-5 py-3 rounded-full transition-all duration-300 flex items-center gap-3 border ${
                                    selectedBrand === brand.name 
                                      ? 'bg-gray-100 text-black shadow-lg scale-105 border-gray-300' 
                                      : 'text-gray-600 hover:bg-gray-50 hover:text-black hover:scale-105 border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-200 p-1">
                                    <img 
                                      src={brand.logo}
                                      alt={brand.name}
                                      className="w-full h-full object-contain"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="hidden w-full h-full flex items-center justify-center text-xs font-bold text-gray-600">
                                      {brand.name.charAt(0)}
                                    </div>
                                  </div>
                                  <span className="font-semibold text-sm">{brand.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Separador visual */}
                        <div className="border-t border-gray-200 my-8"></div>
                        
                        {/* Modelos de la marca seleccionada */}
                        <div>
                          <h3 className="font-semibold mb-6 mt-4 text-lg text-center">
                            Modelos {selectedBrand}
                          </h3>
                          
                          {(() => {
                            const marcaSeleccionada = marcasData.find(m => m.nombre === selectedBrand);
                            const vehiculosMarca = marcaSeleccionada?.vehiculos || [];
                            
                            if (vehiculosMarca.length === 0) {
                              return (
                                <div className="text-center py-8">
                                  <div className="text-gray-500 text-lg mb-2">
                                    🚗 Próximamente
                                  </div>
                                  <p className="text-gray-400 text-sm">
                                    Nuevos modelos de {selectedBrand} llegando pronto
                                  </p>
                                </div>
                              );
                            }
                            
                            return (
                              <div className="flex flex-wrap gap-3 justify-center">
                                {vehiculosMarca.map((vehiculo) => {
                                  // URL simple: /vehiculos/marca/modelo (todo local)
                                  const marcaSlug = selectedBrand.toLowerCase().replace(/\s+/g, '-');
                                  const modeloSlug = vehiculo.modelo.toLowerCase().replace(/\s+/g, '-');
                                  const finalUrl = `/vehiculos/${marcaSlug}/${modeloSlug}`;
                                  return (
                                    <Link 
                                      key={vehiculo.id}
                                      href={finalUrl}
                                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-900 hover:text-white transition-all duration-200 text-sm font-medium border border-gray-200 hover:border-gray-900"
                                      onClick={() => setIsVehiclesOpen(false)}
                                    >
                                      {vehiculo.modelo}
                                    </Link>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    </div>
                  )}
                </li>
                <li>
                  <Link 
                    href="/nosotros" 
                    prefetch={true}
                    className={`text-gray-700 transition-all hover:text-black relative group py-2 ${
                      isLinkActive('/nosotros') ? 'text-black font-semibold' : ''
                    }`}
                  >
                    Nosotros
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-all duration-300 ${
                        isMounted && isLinkActive('/nosotros') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      suppressHydrationWarning={true}
                    ></span>
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/servicios" 
                    prefetch={true}
                    className={`text-gray-700 transition-all hover:text-black relative group py-2 ${
                      isLinkActive('/servicios') ? 'text-black font-semibold' : ''
                    }`}
                  >
                    Servicios
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-all duration-300 ${
                        isMounted && isLinkActive('/servicios') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      suppressHydrationWarning={true}
                    ></span>
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/contactos" 
                    prefetch={true}
                    className={`text-gray-700 transition-all hover:text-black relative group py-2 ${
                      isLinkActive('/contactos') ? 'text-black font-semibold' : ''
                    }`}
                  >
                    Contactos
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-black transition-all duration-300 ${
                        isMounted && isLinkActive('/contactos') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      suppressHydrationWarning={true}
                    ></span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Botón de llamada */}
            <div className="hidden lg:flex items-center">
              <a 
                href="tel:+18095334567"
                className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold"
              >
                Llamar ahora
              </a>
            </div>

            {/* Botón hamburguesa para móvil */}
            <button 
              className="lg:hidden flex flex-col items-center justify-center w-8 h-8 space-y-1"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMounted && isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`} suppressHydrationWarning={true}></span>
              <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMounted && isMobileMenuOpen ? 'opacity-0' : ''}`} suppressHydrationWarning={true}></span>
              <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMounted && isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`} suppressHydrationWarning={true}></span>
            </button>
          </div>
        </div>

        {/* Menú móvil */}
        <HydrationSafe>
          <div className={`lg:hidden bg-white border-t transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}>
            <div className="container mx-auto px-[5%] py-6">
              <ul className="space-y-4">
                <li>
                  <Link 
                    href="/" 
                    className="block text-gray-700 hover:text-black transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Inicio
                  </Link>
                </li>
                <li>
                  <div className="py-2">
                    <span className="block text-gray-700 font-semibold mb-3">Vehículos por Marca</span>
                    <div className="grid grid-cols-2 gap-3 ml-4">
                      {BRANDS_DATA_FALLBACK.map((brand) => (
                        <Link 
                          key={brand.name}
                          href={`/vehiculos/${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center gap-2 text-sm text-gray-600 hover:text-black transition-colors py-1"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 p-1">
                            <img 
                              src={brand.logo} 
                              alt={brand.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-full h-full flex items-center justify-center text-xs font-bold text-gray-600">
                              {brand.name.charAt(0)}
                            </div>
                          </div>
                          {brand.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </li>
                <li>
                  <Link 
                    href="/nosotros" 
                    className="block text-gray-700 hover:text-black transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Nosotros
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/servicios" 
                    className="block text-gray-700 hover:text-black transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Servicios
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/contactos" 
                    className="block text-gray-700 hover:text-black transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contactos
                  </Link>
                </li>
                <li className="pt-4 border-t">
                  <a 
                    href="tel:+18095334567"
                    className="block bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Llamar ahora
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </HydrationSafe>
      </nav>

      {/* Botón Back to Top */}
      <HydrationSafe>
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 z-40 bg-black text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-gray-800 hover:scale-110 ${
            showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          aria-label="Volver arriba"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor" 
            className="w-5 h-5"
          >
            <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04L10.75 5.612V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
          </svg>
        </button>
      </HydrationSafe>


    </>
  );
}
